import {
  Box,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ExternalLink, MapPin } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { ActivityItemLeadSnippetFragment } from '@graphql/__generated__/graphql';
import {
  snippetContainerSx,
  snippetIconColor,
  tooltipProps,
} from '@modules/_shared/components/feed-v2/constants/styles';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import {
  formatAddress,
  formatCurrencyRange,
} from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';

export const ACTIVITY_ITEM_LEAD_SNIPPET_FRAGMENT = gql(/* GraphQL */ `
  fragment ActivityItemLeadSnippet on activities {
    id
    lead {
      id
      created_at
      contact {
        first_name
        last_name
      }
      property {
        route
        street_number
        postcode
        locality
        country_code
        property_type {
          label
        }
        latest_appraisal {
          min
          max
        }
      }
    }
  }
`);

type ActivityItemLeadSnippetProps = {
  activity: ActivityItemLeadSnippetFragment;
};

export const ActivityItemLeadSnippet = ({
  activity,
}: ActivityItemLeadSnippetProps) => {
  const { t, locale } = useLocale();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (activity.lead == null) {
    return null;
  }

  const leadId = activity.lead.id;

  return (
    <Tooltip title={t('Click to view linked lead')} {...tooltipProps}>
      <Box
        onClick={() => window.open(`/leads/${leadId}`, '_blank')}
        sx={snippetContainerSx}
      >
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={isMobile ? 0.25 : 1}
          sx={{
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
            }}
          >
            <ExternalLink
              size={12}
              style={{
                flexShrink: 0,
                color: (theme.vars ?? theme).palette.grey[600],
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                whiteSpace: isMobile ? 'normal' : 'nowrap',
              }}
            >
              {activity.lead.contact?.first_name}{' '}
              {activity.lead.contact?.last_name}
            </Typography>
            {activity.lead.property?.property_type?.label && (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  ·
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {activity.lead.property.property_type.label}
                </Typography>
              </>
            )}
          </Stack>
          {activity.lead.property?.latest_appraisal && (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                color: 'text.primary',
                flexShrink: 0,
              }}
            >
              {formatCurrencyRange(
                activity.lead.property.latest_appraisal,
                getCurrencyByCountryCode(
                  activity.lead.property?.country_code ?? 'CH',
                ),
                locale,
              )}
            </Typography>
          )}
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 0.25,
          }}
        >
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
            }}
          >
            <MapPin
              size={11}
              color={snippetIconColor}
              style={{ flexShrink: 0 }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {activity.lead.property != null
                ? formatAddress(activity.lead.property, ', ')
                : ''}
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            component="span"
            sx={{
              color: 'text.secondary',
              flexShrink: 0,
            }}
          >
            <TimeAgo dateString={activity.lead.created_at} />
          </Typography>
        </Stack>
      </Box>
    </Tooltip>
  );
};
